import { createSelector } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { blotterSlice } from '@/store/blotter/slice.ts';
import { getEspStreamId } from '@/store/esp/utils.ts';
import type { FixingOrder } from '@/store/orders/fixing/state.ts';
import type { Order } from '@/store/orders/state.ts';
import type { AppState } from '@/store/store.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';

export const selectFixingTile = createSelector(
  (state: AppState, tileId: string) => state.orders[tileId] as Order | undefined,
  (order) => (isFixingOrder(order) ? order : undefined),
);

export const isFixingOrder = (order: unknown): order is FixingOrder => {
  return order !== null && typeof order === 'object' && (order as FixingOrder).type === 'FixingOrder';
};

export const selectOrderEntryByIdFromBlotter = createSelector(
  (state: AppState, orderId: string) => blotterSlice.selectors.getOrderEntryById(state, orderId),
  (order) => order,
);

export const selectNotificationEmailsFixing = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (state: AppState) => state.userPreferences,
  (order, userPreference) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return [];
    }

    if (isDefined(order.properties.notificationEmails)) {
      return order.properties.notificationEmails;
    }

    return userPreference.splitNotifications ? userPreference.splitNotificationsEmailsOrders : userPreference.emails;
  },
);

export const selectClientWayFixingData = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    const currency =
      order.properties.notionalCurrencyIndex === 1
        ? order.properties.currencyPair?.currency1
        : order.properties.currencyPair?.currency2;

    return {
      clientWay: order.properties.clientWay,
      notionalCurrencyIndex: order.properties.notionalCurrencyIndex,
      currency,
    };
  },
);

export const selectNotionalDataFixing = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    const currency =
      order.properties.notionalCurrencyIndex === 1
        ? order.properties.currencyPair?.currency1
        : order.properties.currencyPair?.currency2;

    return {
      notional: order.properties.notional,
      notionalCurrencyIndex: order.properties.notionalCurrencyIndex,
      currency,
    };
  },
);

export const selectNotionalFixing = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return {};
    }

    const currency =
      order.properties.notionalCurrencyIndex === 1
        ? order.properties.currencyPair?.currency1
        : order.properties.currencyPair?.currency2;

    return {
      notional: order.properties.notional,
      notionalCurrencyIndex: order.properties.notionalCurrencyIndex,
      currency,
    };
  },
);

export const selectFixingErrors = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.errors;
  },
);

export const selectFixingReference = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.fixingReference;
  },
);

export const selectFixingPriceType = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.fixingPriceType;
  },
);

export const selectFixingDate = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.fixingDateUtc;
  },
);

export const selectFixingDeliveryDate = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.expiryDay;
  },
);

export const selectFixingPlace = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.fixingPlace;
  },
);

export const selectFixingTime = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.fixingTime;
  },
);

export const selectFixingMarginType = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.fixingMarginType;
  },
);

export const selectFixingCommercialMargin = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.commercialMargin;
  },
);

export const selectFixingIsStandardGroup = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.isStandardGroup;
  },
);

export const selectFixingCanModifyMarginInBps = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.canModifyMarginInBps;
  },
);

export const selectFixingIsCcyForcedBidAsk = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.isCcyForcedBidAsk;
  },
);

export const selectFixingTypes = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.fixingTypes;
  },
);

export const selectFixingTimesOfSelectedType = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.fixingTimesOfSelectedType;
  },
);

export const selectFixingSourceNameOfSelectedType = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.sourceNameOfSelectedType;
  },
);

export const selectFixingNotionalCurrencyIndex = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (order) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    return order.properties.notionalCurrencyIndex;
  },
);

export const selectTradeIdFromFixingTile = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (tile, tileId) => {
    if (isNotDefined(tile) || tile.type !== 'FixingOrder') {
      return undefined;
    }

    if (
      tile.properties.status.type === 'creating' ||
      tile.properties.status.type === 'failed' ||
      tile.properties.status.type === 'pending'
    ) {
      console.warn(`[FixingOrderEditionTile] Tile with id ${tileId} is in creating state, no data available.`);
      return undefined;
    }

    return tile.properties.status.tradeId;
  },
);

export const selectSpotFixingData = createSelector(
  (state: AppState, tileId: string) => selectFixingTile(state, tileId),
  (_state: AppState, tileId: string) => tileId,
  (state: AppState, tileId: string) => {
    const order = selectFixingTile(state, tileId);

    if (
      isNotDefined(order) ||
      order.type !== 'FixingOrder' ||
      isNotDefined(order.properties.currencyPair?.tiering) ||
      isNotDefined(order.properties.currencyPair?.currency1) ||
      isNotDefined(order.properties.currencyPair?.currency2)
    ) {
      return undefined;
    }

    const currencyPair = `${order.properties.currencyPair.currency1}/${order.properties.currencyPair.currency2}`;
    const espStreamId = getEspStreamId(currencyPair, order.properties.currencyPair.tiering);

    return state.esp.streams[espStreamId];
  },
  (order, tileId, espPrice) => {
    if (isNotDefined(order) || order.type !== 'FixingOrder') {
      return undefined;
    }

    if (isNotDefined(order.properties.currencyPair?.tiering)) {
      console.warn(`[FixingOrder] Tile with id ${tileId} has no tiering defined.`);
      return undefined;
    }

    return {
      spot:
        espPrice?.status === 'PRICED'
          ? convertPointsToRate(espPrice.midRate, espPrice.currencyPairPrecision)
          : undefined,
      digitCount: espPrice?.status === 'PRICED' ? espPrice.currencyPairPrecision - 1 : undefined,
    };
  },
);
